import React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import { getTokens, brandAlpha } from "../../styles/tokens";

/* Peças do "modal padrão" e da "página padrão" do painel para as telas de cadastro.
 * Tudo sai dos tokens (marca e modo escuro vêm do tema); nada de hex de marca aqui. */

export const usePanelStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const danger = t.semantic.error;
  return {
    /* ---------- Cabeçalho do modal ---------- */
    modalTitle: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      [theme.breakpoints.down("xs")]: { padding: "16px 16px 12px" },
    },
    modalTitleText: { minWidth: 0, flex: 1 },
    modalHeading: {
      margin: 0,
      fontFamily: t.font,
      fontSize: 18,
      fontWeight: 600,
      lineHeight: 1.35,
      letterSpacing: "-0.01em",
      color: t.text,
      overflowWrap: "anywhere",
    },
    modalSubtitle: {
      margin: "3px 0 0",
      fontFamily: t.font,
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 0,
      color: t.textSecondary,
    },
    modalClose: { flex: "none", width: 34, height: 34, margin: "-4px -6px 0 0" },

    // `classes={{ paper }}` do Dialog: no celular o modal ocupa a largura útil
    dialogPaper: {
      [theme.breakpoints.down("xs")]: {
        margin: 12,
        width: "calc(100% - 24px)",
        maxWidth: "calc(100% - 24px) !important",
        maxHeight: "calc(100% - 24px)",
      },
    },
    // <form> entre o Dialog e o DialogContent: mantém o corpo rolando e o rodapé fixo
    modalForm: {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "hidden",
    },
    // abas do tema logo abaixo do título do modal
    modalTabs: {
      flex: "none",
      padding: "0 12px",
      backgroundColor: t.paper,
      borderBottom: `1px solid ${t.line}`,
      "& .MuiTab-root": { minWidth: 0, padding: "10px 13px" },
    },
    modalBody: {
      [theme.breakpoints.down("xs")]: { padding: 16 },
    },

    /* ---------- Corpo do formulário ---------- */
    // Aplicada num contêiner, põe o rótulo de todo campo `outlined` ACIMA do campo
    // (12,5 px/600) sem mexer no JSX de cada TextField/Select.
    labelsAbove: {
      "& .MuiInputLabel-outlined, & .MuiInputLabel-outlined.MuiInputLabel-shrink, & .MuiInputLabel-outlined.MuiInputLabel-marginDense":
        {
          position: "static",
          transform: "none",
          marginBottom: 6,
          fontFamily: t.font,
          fontSize: 12.5,
          fontWeight: 600,
          lineHeight: 1.4,
          color: t.textSecondary,
          pointerEvents: "auto",
          whiteSpace: "normal",
          maxWidth: "100%",
          zIndex: "auto",
        },
      "& .MuiInputLabel-outlined.Mui-error": { color: danger },
      // sem rótulo flutuante não há entalhe na borda
      "& .MuiOutlinedInput-notchedOutline": { top: 0 },
      "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
      // o MUI esconde o placeholder enquanto o rótulo não "sobe"
      "& label[data-shrink=false] + .MuiInputBase-formControl .MuiInputBase-input::placeholder":
        { opacity: "0.5 !important" },
    },
    stack: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: "14px 16px",
      alignItems: "start",
      "& > .MuiFormControl-root, & > .MuiTextField-root, & > .MuiAutocomplete-root .MuiFormControl-root": {
        margin: 0,
      },
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: "14px 16px",
      alignItems: "start",
      "& > .MuiFormControl-root, & > .MuiTextField-root, & > .MuiAutocomplete-root .MuiFormControl-root": {
        margin: 0,
      },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    grid3: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: "14px 16px",
      alignItems: "start",
      "& > .MuiFormControl-root, & > .MuiTextField-root, & > .MuiAutocomplete-root .MuiFormControl-root": {
        margin: 0,
      },
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    span2: { gridColumn: "1 / -1" },

    section: {
      minWidth: 0,
      "& + &": { marginTop: 22, paddingTop: 20, borderTop: `1px solid ${t.line}` },
    },
    sectionHead: { marginBottom: 12 },
    sectionTitle: {
      margin: 0,
      fontFamily: t.font,
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.text,
    },
    sectionHint: {
      margin: "2px 0 0",
      fontFamily: t.font,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: t.textSecondary,
    },

    field: { display: "flex", flexDirection: "column", minWidth: 0 },
    fieldLabel: {
      display: "block",
      marginBottom: 6,
      fontFamily: t.font,
      fontSize: 12.5,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.textSecondary,
    },
    help: {
      margin: "5px 0 0",
      fontFamily: t.font,
      fontSize: 12,
      lineHeight: 1.5,
      color: t.textSecondary,
    },

    /* ---------- Opção de configuração: texto à esquerda, controle à direita ---------- */
    settingRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 20,
      padding: "14px 0",
      minWidth: 0,
      borderBottom: `1px solid ${t.lineRow}`,
      "&:last-child": { borderBottom: "none" },
      [theme.breakpoints.down("xs")]: { flexDirection: "column", alignItems: "stretch", gap: 10 },
    },
    settingText: { minWidth: 0, flex: "1 1 auto" },
    settingLabel: {
      fontFamily: t.font,
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.45,
      color: t.text,
    },
    settingDesc: {
      marginTop: 2,
      fontFamily: t.font,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: t.textSecondary,
    },
    settingControl: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 8,
      minWidth: 0,
      "& .MuiFormControl-root": { margin: 0 },
      [theme.breakpoints.down("xs")]: { justifyContent: "flex-start" },
    },
    // controle largo (select/campo de texto) em vez de chave
    settingControlWide: {
      width: 280,
      maxWidth: "45%",
      "& .MuiFormControl-root": { width: "100%" },
      [theme.breakpoints.down("xs")]: { width: "100%", maxWidth: "100%" },
    },

    /* ---------- Botões ---------- */
    danger: {
      background: danger,
      backgroundColor: danger,
      color: "#ffffff",
      border: "1px solid transparent",
      boxShadow: "none",
      "&:hover": {
        background: brandAlpha(danger, 0.88),
        backgroundColor: brandAlpha(danger, 0.88),
        borderColor: "transparent",
        boxShadow: "none",
      },
    },
    dangerQuiet: {
      color: t.mode === "dark" ? t.status.down.fg : danger,
      "&:hover": { backgroundColor: brandAlpha(danger, t.mode === "dark" ? 0.16 : 0.08) },
    },
    // empurra a ação destrutiva para a esquerda do rodapé do modal
    actionsStart: { marginRight: "auto !important" },

    /* ---------- Página ---------- */
    toolbar: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      marginBottom: 14,
      minWidth: 0,
      "& .MuiFormControl-root": { margin: 0 },
    },
    toolbarGrow: { flex: "1 1 auto" },
    search: {
      flex: "1 1 240px",
      maxWidth: 380,
      minWidth: 0,
      "& .MuiInputAdornment-root": { color: t.textMuted },
      [theme.breakpoints.down("xs")]: { maxWidth: "100%" },
    },
    tableCard: {
      minWidth: 0,
      overflow: "auto",
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.card,
      ...theme.scrollbarStyles,
      // a última linha não precisa de filete: a borda do cartão já fecha
      "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": { borderBottom: "none" },
    },
    tableCardFill: { flex: 1, minHeight: 0 },
    rowActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 2,
      whiteSpace: "nowrap",
      "& .MuiIconButton-root": { width: 34, height: 34, padding: 0, color: t.textSecondary },
      "& .MuiIconButton-root:hover": { color: t.text },
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    cellMuted: { color: t.textSecondary },
    cellStrong: { fontWeight: 600, color: t.text },
    // bolinha de cor que vem de DADO (fila, tag, conexão)
    colorDot: {
      display: "inline-block",
      flex: "none",
      width: 10,
      height: 10,
      borderRadius: "50%",
      boxShadow: `inset 0 0 0 1px ${brandAlpha("#000000", 0.12)}`,
    },
    notice: {
      padding: "10px 12px",
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
      backgroundColor: t.muted,
      fontFamily: t.font,
      fontSize: 12.5,
      lineHeight: 1.55,
      color: t.textSecondary,
    },
  };
});

/**
 * Cabeçalho do modal padrão: título curto, subtítulo opcional em texto secundário
 * e, se vier `onClose`, o "X" à direita. Substitui o `<DialogTitle>` cru.
 */
export function ModalTitle({ title, subtitle, onClose, id, className, children }) {
  const c = usePanelStyles();
  return (
    <DialogTitle disableTypography id={id} className={clsx(c.modalTitle, className)}>
      <div className={c.modalTitleText}>
        <h2 className={c.modalHeading}>{title}</h2>
        {subtitle && <p className={c.modalSubtitle}>{subtitle}</p>}
        {children}
      </div>
      {onClose && (
        <IconButton className={c.modalClose} size="small" aria-label="Fechar" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </DialogTitle>
  );
}

/** Seção de formulário com título pequeno; seções seguidas ganham filete entre si. */
export function FormSection({ title, description, actions, className, children }) {
  const c = usePanelStyles();
  return (
    <section className={clsx(c.section, className)}>
      {(title || description || actions) && (
        <div className={c.sectionHead} style={actions ? { display: "flex", alignItems: "flex-start", gap: 12 } : undefined}>
          <div style={{ minWidth: 0, flex: 1 }}>
            {title && <h3 className={c.sectionTitle}>{title}</h3>}
            {description && <p className={c.sectionHint}>{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** Rótulo acima de um controle qualquer (chave, grupo de botões, seletor de cor…), com ajuda opcional. */
export function FieldLabel({ label, help, htmlFor, className, children }) {
  const c = usePanelStyles();
  return (
    <div className={clsx(c.field, className)}>
      {label && (
        <label className={c.fieldLabel} htmlFor={htmlFor}>
          {label}
        </label>
      )}
      {children}
      {help && <p className={c.help}>{help}</p>}
    </div>
  );
}

/** Linha de opção: rótulo + descrição curta à esquerda, controle à direita (`wide` para select/campo). */
export function SettingRow({ label, description, wide = false, className, children }) {
  const c = usePanelStyles();
  return (
    <div className={clsx(c.settingRow, className)}>
      <div className={c.settingText}>
        <div className={c.settingLabel}>{label}</div>
        {description && <div className={c.settingDesc}>{description}</div>}
      </div>
      <div className={clsx(c.settingControl, wide && c.settingControlWide)}>{children}</div>
    </div>
  );
}

/** Ação destrutiva: vermelho dos tokens. `variant="contained"` (padrão) ou `"text"`. */
export const DangerButton = React.forwardRef(function DangerButton(
  { variant = "contained", className, ...rest },
  ref
) {
  const c = usePanelStyles();
  return (
    <Button
      ref={ref}
      variant={variant}
      className={clsx(variant === "contained" ? c.danger : c.dangerQuiet, className)}
      {...rest}
    />
  );
});

/** Barra de filtros da página: busca + selects numa linha, quebrando no celular. */
export function FilterBar({ className, children }) {
  const c = usePanelStyles();
  return <div className={clsx(c.toolbar, className)}>{children}</div>;
}

/** Campo de busca da barra de filtros (lupa à esquerda, 40 px). */
export function SearchField({ value, onChange, placeholder, className, ...rest }) {
  const c = usePanelStyles();
  return (
    <TextField
      type="search"
      size="small"
      variant="outlined"
      className={clsx(c.search, className)}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      inputProps={{ "aria-label": placeholder }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  );
}

/** Cartão que hospeda a tabela do tema; rola por dentro quando a tabela é larga. `fill` ocupa a altura que sobrar. */
export const TableCard = React.forwardRef(function TableCard(
  { fill = false, className, children, ...rest },
  ref
) {
  const c = usePanelStyles();
  return (
    <div ref={ref} className={clsx(c.tableCard, fill && c.tableCardFill, className)} {...rest}>
      {children}
    </div>
  );
});

/** Ações por linha da tabela: ícones (com Tooltip) alinhados à direita. */
export function RowActions({ className, children }) {
  const c = usePanelStyles();
  return <div className={clsx(c.rowActions, className)}>{children}</div>;
}
